import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import {
  MdArrowBack,
  MdLogin,
  MdOutlineWhatsapp,
  MdOutlineEmail,
  MdPushPin,
  MdOutlinePushPin,
  MdDeleteOutline,
  MdCheckCircleOutline,
  MdOutlineHistory,
  MdTrendingUp,
  MdTrendingDown,
  MdOutlineTaskAlt,
  MdStar,
  MdOutlineFileDownload,
  MdOutlineEdit,
  MdOutlineBusinessCenter,
  MdOutlineCelebration,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { APP_LOGIN_URL, CLIENTS, PROFESSIONALS, STAFF } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import useCrmAccount from "../components/crm/useCrmAccount";
import AccountCharts from "../components/crm/AccountCharts";
import BillingPanel from "../components/crm/BillingPanel";
import ClientSuggestions from "../components/crm/ClientSuggestions";
import Modal from "../components/modals/Modal";
import CompanySupportForm from "../components/forms/CompanySupportForm";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonHeader,
  SkeletonKpiStrip,
  SkeletonChart,
  SkeletonPanel,
  SkeletonWorkList,
} from "../components/crm/Skeletons";
import {
  formatDate,
  formatDaysCount,
  formatMoney,
  formatNumber,
  formatRelativeDays,
  initialsOf,
  INTERACTION_LABELS,
  INTERACTION_TYPES,
  PLAN_TONE,
  PRIORITY_OPTIONS,
  whatsappLink,
} from "../components/crm/crmHelpers";

/*
  Ficha da conta.

  A versão anterior era uma pilha de formulários: a tela abria com dois campos
  de digitação ocupando a coluna esquerda inteira, cinco números soltos no topo
  e nenhum gráfico — enquanto o endpoint `GET /clients/:id` já devolvia 16
  meses de série de uso, histórico de pagamento, avaliações e catálogo, tudo
  sem ser exibido.

  A ordem de leitura agora é a de uma ficha de conta de verdade:

  1. **Quem é** — faixa de identidade com documento, canal de aquisição e
     tempo de casa.
  2. **Como está** — indicadores com VARIAÇÃO, não valor absoluto solto. "89
     agendamentos" não diz nada; "89, 18% abaixo do mês anterior" diz tudo.
  3. **Como chegou aqui** — a curva de uso. É o que transforma a ficha de
     cadastro em diagnóstico.
  4. **O que fazer** — relacionamento e histórico, abaixo da dobra de leitura
     mas na mesma tela.
*/

const TYPE_TONE = {
  CALL: "info",
  WHATSAPP: "success",
  EMAIL: "info",
  MEETING: "warning",
  NOTE: "neutral",
  SYSTEM: "neutral",
};

/*
  `permissionId` do profissional (tabela Permissions do app). O painel mostrava
  o código cru, e "USER_CALENDARS" não diz a ninguém que aquela pessoa vê a
  agenda de toda a equipe.
*/
const ROTULO_PAPEL = {
  ADMIN: "Administrador",
  POWER_USER: "Acesso total",
  USER_CALENDARS: "Vê todas as agendas",
  USER: "Profissional",
};

const ROTULO_CANAL = {
  instagram: "Instagram",
  tiktok: "TikTok",
  indicacao: "Indicação",
  google: "Google",
  loja: "Loja de apps",
  outro: "Outro",
};

/* -------------------------------------------------------------- indicadores */

/*
  Indicador com variação.

  O `delta` é sempre relativo ao período anterior equivalente. Sem ele, um
  número isolado não se lê: 89 agendamentos é bom para um salão de uma cadeira
  e ruim para um de quatro, mas "18% abaixo do mês passado" é ruim para
  qualquer um.
*/
const Kpi = ({ label, value, hint, delta, tone = "neutral", icon: Icon }) => {
  const subindo = delta > 0;
  const Seta = subindo ? MdTrendingUp : MdTrendingDown;

  return (
    <div className="flex-1 border-slate-200 px-5 py-4 sm:border-r sm:last:border-r-0">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </p>
      <p className="mt-1 flex items-baseline gap-2">
        <span className="text-xl font-bold leading-none text-slate-900">
          {value}
        </span>
        {delta !== undefined && delta !== null && Number.isFinite(delta) ? (
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
              subindo ? "text-emerald-600" : "text-rose-600"
            }`}
          >
            <Seta size={13} />
            {Math.abs(Math.round(delta))}%
          </span>
        ) : null}
      </p>
      {hint ? (
        <p className={`mt-0.5 text-[11px] ${tone === "danger" ? "text-rose-600" : "text-slate-500"}`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

const Dado = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="shrink-0 text-xs text-slate-500">{label}</span>
    <span className="truncate text-right text-xs font-semibold text-slate-800">
      {value || "—"}
    </span>
  </div>
);

/* ------------------------------------------------------------------ timeline */

const TimelineItem = ({ item, onTogglePin, onDelete, canDelete, stageLabel }) => {
  if (item.kind === "STAGE") {
    return (
      <li className="relative pl-6">
        <span className="absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <MdTrendingUp size={11} />
        </span>
        <p className="text-sm text-slate-700">
          Estágio:{" "}
          <span className="font-semibold">
            {item.previousStage ? stageLabel(item.previousStage) : "primeira classificação"}
          </span>{" "}
          → <span className="font-semibold">{stageLabel(item.stage)}</span>
          {item.daysInPreviousStage !== null
            ? ` após ${formatDaysCount(item.daysInPreviousStage)}`
            : ""}
        </p>
        <p className="text-[11px] text-slate-400">
          {formatDate(item.at)} · {item.authorName}
        </p>
      </li>
    );
  }

  if (item.kind === "TASK") {
    return (
      <li className="relative pl-6">
        <span
          className={`absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full ${
            item.status === "DONE"
              ? "bg-emerald-50 text-emerald-500"
              : "bg-sky-50 text-sky-500"
          }`}
        >
          <MdOutlineTaskAlt size={11} />
        </span>
        <p className="text-sm text-slate-800">
          <span className="font-semibold">Tarefa</span> · {item.title}
        </p>
        <p className="text-[11px] text-slate-400">
          {formatDate(item.at)} ·{" "}
          {item.status === "OPEN"
            ? `aberta para ${item.assigneeName}`
            : item.status === "DONE"
            ? "concluída"
            : "cancelada"}
        </p>
      </li>
    );
  }

  return (
    <li className="group relative pl-6">
      <span className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-white bg-pink-400 ring-1 ring-pink-100" />
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="mb-0.5 flex items-center gap-1.5">
            <Badge tone={TYPE_TONE[item.type]}>
              {INTERACTION_LABELS[item.type] || item.type}
            </Badge>
            {item.pinned ? (
              <MdPushPin size={12} className="text-pink-600" title="Fixada" />
            ) : null}
          </p>
          <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
            {item.body}
          </p>
          <p className="text-[11px] text-slate-400">
            {formatDate(item.at)} · {item.authorName}
          </p>
        </div>

        {item.type !== "SYSTEM" ? (
          <div className="flex shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100">
            <button
              type="button"
              onClick={() => onTogglePin(item.id, !item.pinned)}
              title={item.pinned ? "Desafixar" : "Fixar no topo"}
              className={`rounded-md p-1 transition hover:bg-pink-50 ${
                item.pinned ? "text-pink-600" : "text-slate-300 hover:text-pink-600"
              }`}
            >
              {item.pinned ? <MdPushPin size={15} /> : <MdOutlinePushPin size={15} />}
            </button>
            {canDelete(item) ? (
              <button
                type="button"
                onClick={() => onDelete(item.id)}
                title="Apagar"
                className="rounded-md p-1 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
              >
                <MdDeleteOutline size={15} />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
};

/* ---------------------------------------------------------------- a página */

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, isAdmin } = useApplication();

  /*
    Voltar para a tela ANTERIOR, não para um destino fixo.

    O botão apontava para /pipeline sempre — então quem chegava pela tabela de
    clientes, por uma fila ou pelo relatório de uma campanha era jogado no
    pipeline e perdia o filtro, a página e a posição da lista.

    `location.key === "default"` significa que esta é a primeira entrada do
    histórico (link colado, aba nova): aí não há para onde voltar e o destino
    é a lista de clientes.
  */
  const voltar = () =>
    location.key === "default" ? navigate("/clientes") : navigate(-1);

  const [noteType, setNoteType] = useState("CALL");
  const [noteBody, setNoteBody] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDueDate, setTaskDueDate] = useState("");
  const [taskPriority, setTaskPriority] = useState("NORMAL");
  const [taskAssignee, setTaskAssignee] = useState("");
  const [exportando, setExportando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [personificando, setPersonificando] = useState(null);
  const [erroPersonificar, setErroPersonificar] = useState("");

  /*
    Entrar no app como a profissional, para ver o que ela vê.

    POST numa rota própria (não o GET do cadastro): a chamada cria a sessão e
    registra em SupportAuditLog quem abriu a conta de quem. Só ADMIN — para
    gerente de contas o servidor responde 403 e a mensagem dele aparece aqui.

    Aba nova para não derrubar a sessão do painel: o token do salão e o do
    staff moram na MESMA chave `token` do localStorage, em origens diferentes
    em produção. Navegar na mesma aba sobrescreveria um com o outro.
  */
  const entrarComo = async (profissional) => {
    setErroPersonificar("");
    setPersonificando(profissional.id);

    try {
      const { data: resposta } = await axios.post(
        `${PROFESSIONALS.IMPERSONATE}/${profissional.id}/impersonate`
      );

      window.open(
        `${APP_LOGIN_URL}?token=${resposta.data.token}`,
        "_blank",
        "noopener"
      );
    } catch (erro) {
      setErroPersonificar(
        erro.response?.data?.message ||
          "Não foi possível abrir a sessão de suporte."
      );
    } finally {
      setPersonificando(null);
    }
  };

  /*
    Exportação da base de clientes finais do salão. Estava no modal de
    detalhes da tabela, que saiu quando o olho passou a abrir esta página —
    a ação precisava vir junto, senão sumia sem substituto.
  */
  const exportarClientes = async () => {
    setExportando(true);
    try {
      const resposta = await axios.get(
        `${CLIENTS.EXPORT_CUSTOMERS}/${id}/customers/export`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([resposta.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clientes-${id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExportando(false);
    }
  };

  const {
    relationship,
    account: data,
    usage,
    error: actionError,
    addInteraction,
    addTask,
    setTaskStatus,
    togglePin,
    removeInteraction,
    stageLabel,
  } = useCrmAccount(id);

  /*
    A ficha completa vem de GET /clients/:id — série de uso, histórico de
    pagamento, avaliações, catálogo. Este endpoint já existia e devolvia tudo
    isso; a tela anterior usava a LISTAGEM filtrada por #id e jogava fora o
    resto.
  */
  const detail = useQuery(["client", "detail", id], async () => {
    const { data: response } = await axios.get(`${CLIENTS.ONE}/${id}`);
    return response.data;
  });

  const { data: staffOptions } = useQuery(
    ["staff", "options"],
    async () => {
      const { data: response } = await axios.get(STAFF.OPTIONS);
      return response.data;
    },
    { enabled: isAdmin }
  );

  if (relationship.isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonHeader />
        <SkeletonKpiStrip count={6} withIcon={false} />
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <SkeletonChart height={240} bars={14} className="xl:col-span-2" />
          <div className="space-y-4">
            <SkeletonPanel lines={7} />
            <SkeletonPanel lines={5} />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <SkeletonPanel lines={4} />
          <SkeletonWorkList rows={4} withHeader={false} />
        </div>
      </div>
    );
  }

  if (relationship.error) {
    return (
      <ErrorBlock
        message={
          relationship.error.response?.data?.message ||
          "Não foi possível carregar a ficha deste cliente."
        }
        onRetry={relationship.refetch}
      />
    );
  }

  const company = data.company;
  const info = detail.data;
  const ficha = info?.company || {};

  /*
    A equipe já vinha no payload de GET /clients/:id (getCompanyDetails) e
    nunca era exibida. Vem ordenada pelo servidor — excluídos por último,
    ativos primeiro, depois por nome — então a ordem não é refeita aqui.
  */
  const profissionais = info?.professionals || [];
  const ativos = profissionais.filter(
    (pessoa) => pessoa.isActive && !pessoa.isExcluded
  ).length;
  const lifetime = info?.lifetimeSeries || [];
  const uso = info?.usageSeries || [];
  const phoneLink = whatsappLink(usage?.phoneNumber || ficha.phoneNumber);

  /*
    Tendência entre os dois últimos meses FECHADOS.

    Comparar o mês corrente com o anterior é a armadilha clássica do painel:
    no dia 4, outubro tem 4 dias de dados contra os 30 de setembro, e a conta
    mais saudável da base aparece com "-92%". O mês em andamento (o último da
    série) é descartado por isso.
  */
  const tendencia = (() => {
    if (lifetime.length < 3) return null;

    const fechado = lifetime[lifetime.length - 2];
    const anterior = lifetime[lifetime.length - 3];
    if (!anterior?.appointments) return null;

    return {
      mes: fechado.label,
      total: fechado.appointments,
      variacao:
        ((fechado.appointments - anterior.appointments) / anterior.appointments) *
        100,
    };
  })();

  const canDelete = (item) =>
    isAdmin || Number(item.authorId) === Number(user?.id);

  return (
    <div className="space-y-4">
      {/* ---------------------------------------------------------- identidade */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-start gap-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4">
          <button
            type="button"
            onClick={voltar}
            className="mt-1 shrink-0 text-slate-400 transition hover:text-pink-600"
            title="Voltar para a tela anterior"
          >
            <MdArrowBack size={20} />
          </button>

          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-pink-600 text-base font-bold text-white shadow-sm">
            {initialsOf(company.name)}
          </span>

          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-bold leading-tight text-slate-900">
              {company.name}
            </h1>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
              <span className="font-mono">#{company.id}</span>
              {ficha.documentId ? <span>· {ficha.documentId}</span> : null}
              {ficha.acquisitionSource ? (
                <span>· veio do {ROTULO_CANAL[ficha.acquisitionSource] || ficha.acquisitionSource}</span>
              ) : null}
              {ficha.createdAt ? (
                <span>· cliente há {formatDaysCount(usage?.daysSinceRegistration)}</span>
              ) : null}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {usage ? (
                <>
                  <Badge tone={PLAN_TONE[usage.planId] || "neutral"}>{usage.planId}</Badge>
                  <Badge tone={usage.stageTone}>{usage.stageLabel}</Badge>
                </>
              ) : null}
              <span className="text-[11px] text-slate-500">
                {usage?.ownerName ? `carteira de ${usage.ownerName}` : "sem responsável"}
              </span>
              {/* Evento de onde veio. A tela do evento é só do ADMIN; o gerente
                  de contas vê o nome, sem o atalho. */}
              {data.event ? (
                isAdmin ? (
                  <Link
                    to={`/eventos/${data.event.id}`}
                    title="Abrir o evento"
                    className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-0.5 text-xs font-semibold text-pink-700 ring-1 ring-inset ring-pink-200 hover:bg-pink-100"
                  >
                    <MdOutlineCelebration size={13} /> Veio pelo evento {data.event.name}
                  </Link>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-0.5 text-xs font-semibold text-pink-700 ring-1 ring-inset ring-pink-200">
                    <MdOutlineCelebration size={13} /> Veio pelo evento {data.event.name}
                  </span>
                )
              ) : null}
            </div>
          </div>

          <div className="flex shrink-0 gap-2">
            {phoneLink ? (
              <a
                href={phoneLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                <MdOutlineWhatsapp size={16} /> WhatsApp
              </a>
            ) : null}
            {isAdmin ? (
              <button
                type="button"
                onClick={() => setEditando(true)}
                title="Editar o cadastro do salão"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200"
              >
                <MdOutlineEdit size={16} /> Editar cadastro
              </button>
            ) : null}
            {isAdmin ? (
              <button
                type="button"
                onClick={exportarClientes}
                disabled={exportando}
                title="Baixar a base de clientes deste salão em CSV"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200 disabled:opacity-40"
              >
                <MdOutlineFileDownload size={16} />
                {exportando ? "Exportando..." : "Clientes"}
              </button>
            ) : null}
            {(usage?.email || ficha.email) ? (
              <a
                href={`mailto:${usage?.email || ficha.email}`}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:border-pink-200"
              >
                <MdOutlineEmail size={16} /> E-mail
              </a>
            ) : null}
          </div>
        </div>

        {/* ------------------------------------------------------ indicadores */}
        <div className="flex flex-col divide-y divide-slate-100 sm:flex-row sm:divide-y-0">
          <Kpi
            label="Mensalidade"
            value={formatMoney(usage?.monthlyValue || 0)}
            hint={`${formatMoney(usage?.totalPaid || 0)} pagos até hoje`}
          />
          <Kpi
            label="Vencimento"
            value={
              usage?.planExpirationDate ? formatDate(usage.planExpirationDate) : "—"
            }
            hint={
              usage?.planExpirationDate
                ? formatRelativeDays(usage.planExpirationDate)
                : "—"
            }
            tone={usage?.daysToExpire <= 7 ? "danger" : "neutral"}
          />
          <Kpi
            label="Agend. 30 dias"
            value={formatNumber(usage?.appointmentsLast30Days || 0)}
            hint={`${formatNumber(usage?.appointmentCount || 0)} desde o início`}
          />
          <Kpi
            label="Base"
            value={`${formatNumber(usage?.professionalCount || 0)} / ${formatNumber(
              info?.customersCount ?? usage?.customerCount ?? 0
            )}`}
            hint="profissionais / clientes finais"
          />
          <Kpi
            label="Avaliação"
            icon={MdStar}
            value={
              info?.reviews?.total
                ? info.reviews.average.toFixed(1).replace(".", ",")
                : "—"
            }
            hint={
              info?.reviews?.total
                ? `${formatNumber(info.reviews.total)} avaliações`
                : "sem avaliações"
            }
          />
          <Kpi
            label="Último contato"
            value={
              data.lastContactAt ? formatRelativeDays(data.lastContactAt) : "nunca"
            }
            hint={`${formatNumber(data.contactCount)} registro(s)`}
            tone={data.lastContactAt ? "neutral" : "danger"}
          />
        </div>
      </Card>

      {actionError ? <ErrorBlock message={actionError} /> : null}

      {data.pinned.length > 0 ? (
        <Card className="border-l-4 border-l-pink-500 p-4">
          <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-pink-700">
            <MdPushPin size={13} /> Leia antes de falar com ela
          </p>
          <ul className="space-y-1.5">
            {data.pinned.map((note) => (
              <li key={note.id} className="text-sm text-slate-800">
                <span className="whitespace-pre-wrap">{note.body}</span>
                <span className="ml-1 text-[11px] text-slate-500">
                  — {note.authorName}, {formatDate(note.at)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* ------------------------------------------- uso + dados cadastrais */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="overflow-hidden xl:col-span-2">
          <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Uso ao longo do tempo</h2>
              <p className="text-[11px] text-slate-500">
                Agendamentos e comandas por mês desde o cadastro
              </p>
            </div>

            {tendencia ? (
              <p className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="font-semibold text-slate-700">
                  {tendencia.mes} fechou em {formatNumber(tendencia.total)}
                </span>
                <span
                  className={`inline-flex items-center gap-0.5 font-semibold ${
                    tendencia.variacao >= 0 ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {tendencia.variacao >= 0 ? (
                    <MdTrendingUp size={14} />
                  ) : (
                    <MdTrendingDown size={14} />
                  )}
                  {Math.abs(Math.round(tendencia.variacao))}%
                </span>
                <span>vs. mês anterior</span>
              </p>
            ) : null}
          </div>

          {detail.isLoading ? (
            <SkeletonChart height={240} bars={14} withHeader={false} />
          ) : (
            <AccountCharts lifetimeSeries={lifetime} usageSeries={uso} />
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="mb-2 text-sm font-bold text-slate-900">Dados da conta</h2>
            <div className="divide-y divide-slate-100">
              <Dado label="E-mail" value={usage?.email || ficha.email} />
              <Dado label="Telefone" value={usage?.phoneNumber || ficha.phoneNumber} />
              <Dado label="Documento" value={ficha.documentId} />
              <Dado label="Cadastro" value={formatDate(ficha.createdAt)} />
              <Dado
                label="Canal"
                value={ROTULO_CANAL[ficha.acquisitionSource] || ficha.acquisitionSource}
              />
              <Dado label="Cupom" value={ficha.cupom} />
              <Dado label="Plano" value={ficha.planId} />
              {/*
                Periodicidade contratada. O rótulo vem do servidor
                (`billingCycleLabel`, montado a partir de config/planCatalog),
                e não de um mapa local: o painel dizendo "Semestral" e a
                cobrança chamando o mesmo ciclo de outra coisa é divergência
                que o catálogo único existe para impedir.

                Vazio quando a conta nunca contratou nada — ou pagou antes de
                a coluna existir e o backfill não achou o ciclo no histórico.
              */}
              <Dado label="Periodicidade" value={ficha.billingCycleLabel} />
              <Dado label="Vencimento do plano" value={formatDate(ficha.planExpirationDate)} />
              <Dado
                label="Limite de usuários"
                value={ficha.numberOfUsers ? `${ficha.numberOfUsers} assento(s)` : null}
              />
            </div>
          </Card>

          <BillingPanel
            companyId={id}
            paymentHistory={info?.paymentHistory || []}
            summary={info?.paymentHistorySummary}
          />

          <Card className="p-4">
            <h2 className="mb-2 text-sm font-bold text-slate-900">Catálogo</h2>
            <div className="divide-y divide-slate-100">
              <Dado label="Serviços" value={formatNumber(info?.servicesCount || 0)} />
              <Dado label="Produtos" value={formatNumber(info?.productsCount || 0)} />
              <Dado label="Categorias" value={formatNumber(info?.categoriesCount || 0)} />
              {/*
                `commandsCount` do endpoint vem AGRUPADO por status
                ([{statusId, count}, ...]), não como total — renderizá-lo como
                número dava 0 ao lado de um gráfico mostrando centenas. O total
                limpo é `metrics.commandCount`, o mesmo que alimenta a tabela
                de clientes.
              */}
              <Dado
                label="Comandas"
                value={formatNumber(info?.metrics?.commandCount || 0)}
              />
              <Dado label="Fichas de cliente" value={formatNumber(info?.recordsCount || 0)} />
            </div>
          </Card>
        </div>
      </div>

      {/* ------------------------------------------------------------ equipe */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-end justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Equipe</h2>
            <p className="text-[11px] text-slate-500">
              {profissionais.length} cadastrado(s) · {ativos} ativo(s)
              {ficha.numberOfUsers
                ? ` · ${ficha.numberOfUsers} assento(s) contratado(s)`
                : ""}
            </p>
          </div>

          {isAdmin ? (
            <p className="text-[11px] text-slate-400">
              Entrar como abre o app do salão em aba nova e vale 1 hora · fica
              registrado na auditoria
            </p>
          ) : null}
        </div>

        {erroPersonificar ? (
          <div className="px-4 pt-3">
            <ErrorBlock message={erroPersonificar} />
          </div>
        ) : null}

        {profissionais.length ? (
          <ul className="divide-y divide-slate-100">
            {profissionais.map((pessoa) => {
              /*
                O app relê o profissional do banco a cada request e recusa
                inativo ou excluído (ver authenticateJWT). Personificar um
                deles abriria uma aba que cai no login no primeiro clique —
                então o botão sai de cena e a tela diz por quê.
              */
              const podeEntrar = pessoa.isActive && !pessoa.isExcluded;
              const entrando = personificando === pessoa.id;

              return (
                <li
                  key={pessoa.id}
                  className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                    pessoa.isExcluded ? "opacity-60" : ""
                  }`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-[11px] font-bold text-pink-700">
                    {initialsOf(pessoa.name)}
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-slate-900">
                      <span className="truncate">{pessoa.name}</span>
                      <Badge
                        tone={pessoa.permissionId === "USER" ? "neutral" : "info"}
                      >
                        {ROTULO_PAPEL[pessoa.permissionId] || pessoa.permissionId}
                      </Badge>
                      {pessoa.isExcluded ? (
                        <Badge tone="danger">excluído</Badge>
                      ) : !pessoa.isActive ? (
                        <Badge tone="warning">desativado</Badge>
                      ) : null}
                    </p>
                    <p className="truncate text-[11px] text-slate-500">
                      {[pessoa.email, pessoa.phoneNumber]
                        .filter(Boolean)
                        .join(" · ") || "sem contato cadastrado"}
                    </p>
                  </div>

                  <span className="shrink-0 text-[11px] text-slate-400">
                    desde {formatDate(pessoa.createdAt)}
                  </span>

                  {isAdmin ? (
                    podeEntrar ? (
                      <button
                        type="button"
                        onClick={() => entrarComo(pessoa)}
                        disabled={entrando}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <MdLogin size={14} />
                        {entrando ? "Abrindo..." : "Entrar como"}
                      </button>
                    ) : (
                      <span className="shrink-0 text-[11px] text-slate-400">
                        sem acesso ao app
                      </span>
                    )
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState
            icon={MdOutlineBusinessCenter}
            title="Nenhum profissional cadastrado"
            description="A conta existe mas ninguém da equipe foi cadastrado ainda — é o caso clássico de conta que nunca saiu do papel."
          />
        )}
      </Card>

      {/* -------------------------------------------------------- sugestões */}
      <ClientSuggestions companyId={id} />

      {/* -------------------------------------- relacionamento + histórico */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-sm font-bold text-slate-900">Registrar contato</h2>
            <p className="mb-2 text-[11px] text-slate-500">
              O que foi dito fica com a conta, não no WhatsApp de quem atendeu
            </p>

            <div className="flex flex-wrap gap-1.5">
              {INTERACTION_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setNoteType(option.value)}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset transition ${
                    noteType === option.value
                      ? "bg-pink-600 text-white ring-pink-600"
                      : "bg-white text-slate-600 ring-slate-200 hover:ring-pink-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <textarea
              value={noteBody}
              onChange={(event) => setNoteBody(event.target.value)}
              rows={3}
              maxLength={5000}
              placeholder="O que ela disse, o que ficou combinado..."
              className="mt-2 w-full resize-y rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            />

            <button
              type="button"
              onClick={() =>
                addInteraction.mutate(
                  { type: noteType, body: noteBody },
                  { onSuccess: () => setNoteBody("") }
                )
              }
              disabled={!noteBody.trim() || addInteraction.isLoading}
              className="mt-2 w-full rounded-xl bg-pink-600 py-2 text-sm font-semibold text-white transition hover:bg-pink-700 disabled:opacity-40"
            >
              {addInteraction.isLoading ? "Salvando..." : "Registrar"}
            </button>
          </Card>

          <Card className="p-4">
            <h2 className="text-sm font-bold text-slate-900">Tarefas</h2>
            <p className="mb-2 text-[11px] text-slate-500">
              Compromisso com data e dono — tira a conta das filas automáticas
            </p>

            {data.openTasks.length > 0 ? (
              <ul className="mb-3 space-y-1.5">
                {data.openTasks.map((task) => (
                  <li
                    key={task.id}
                    className="flex items-start gap-2 rounded-xl bg-slate-50 p-2.5"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setTaskStatus.mutate({ taskId: task.id, status: "DONE" })
                      }
                      title="Marcar como feita"
                      className="mt-0.5 shrink-0 text-slate-300 transition hover:text-emerald-600"
                    >
                      <MdCheckCircleOutline size={18} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800">{task.title}</p>
                      <p className="text-[11px] text-slate-500">
                        {task.assigneeName}
                        {task.dueDate ? ` · vence ${formatRelativeDays(task.dueDate)}` : ""}
                      </p>
                    </div>
                    {task.isOverdue ? <Badge tone="danger">atrasada</Badge> : null}
                  </li>
                ))}
              </ul>
            ) : null}

            <input
              value={taskTitle}
              onChange={(event) => setTaskTitle(event.target.value)}
              maxLength={180}
              placeholder="Ex.: ligar para fechar o upgrade"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            />

            <div className="mt-2 grid grid-cols-2 gap-2">
              <input
                type="date"
                value={taskDueDate}
                onChange={(event) => setTaskDueDate(event.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300"
              />
              <select
                value={taskPriority}
                onChange={(event) => setTaskPriority(event.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300"
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {isAdmin ? (
              <select
                value={taskAssignee}
                onChange={(event) => setTaskAssignee(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300"
              >
                <option value="">Responsável: dono da carteira (ou eu)</option>
                {(staffOptions || []).map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            ) : null}

            <button
              type="button"
              onClick={() =>
                addTask.mutate(
                  {
                    title: taskTitle,
                    dueDate: taskDueDate || null,
                    priority: taskPriority,
                    assignedToUserId: taskAssignee || undefined,
                  },
                  {
                    onSuccess: () => {
                      setTaskTitle("");
                      setTaskDueDate("");
                      setTaskPriority("NORMAL");
                    },
                  }
                )
              }
              disabled={!taskTitle.trim() || addTask.isLoading}
              className="mt-2 w-full rounded-xl border border-pink-200 py-2 text-sm font-semibold text-pink-700 transition hover:bg-pink-50 disabled:opacity-40"
            >
              {addTask.isLoading ? "Criando..." : "Criar tarefa"}
            </button>
          </Card>
        </div>

        <Card className="overflow-hidden xl:col-span-2">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-bold text-slate-900">Histórico de relacionamento</h2>
            <p className="text-[11px] text-slate-500">
              Contatos, tarefas e mudanças de estágio na mesma linha do tempo
            </p>
          </div>

          {data.timeline.length === 0 ? (
            <EmptyState
              icon={MdOutlineHistory}
              title="Nada registrado ainda"
              description="Registre o primeiro contato ao lado. A partir daí o histórico fica com a conta, e não com quem atendeu."
            />
          ) : (
            /*
              Trilho vertical em vez de linhas separadas: a timeline passa a se
              ler como uma sequência, e não como uma lista de registros soltos.
            */
            <ul className="relative space-y-4 px-4 py-4 before:absolute before:bottom-4 before:left-[23px] before:top-4 before:w-px before:bg-slate-200">
              {data.timeline.map((item) => (
                <TimelineItem
                  key={`${item.kind}-${item.id}`}
                  item={item}
                  stageLabel={stageLabel}
                  canDelete={canDelete}
                  onTogglePin={(interactionId, pinned) =>
                    togglePin.mutate({ interactionId, pinned })
                  }
                  onDelete={(interactionId) => removeInteraction.mutate(interactionId)}
                />
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/*
        Mesmo formulário usado na tabela de clientes. Ele busca os dados
        completos sozinho a partir do id, então reaproveitar aqui não exige
        carregar nada antes — e evita duas telas de edição que divergiriam na
        primeira mudança de campo.
      */}
      <Modal
        isOpen={editando}
        onClose={setEditando}
        size="lg"
        icon={MdOutlineBusinessCenter}
        title="Editar cliente"
        subtitle={`${company.name} · #${company.id}`}
        bodyClassName="bg-slate-50/50"
      >
        <CompanySupportForm
          company={company}
          type="EDIT"
          isDrawerOpen={editando}
          onClose={setEditando}
          owners={staffOptions || []}
          canAssignOwner={isAdmin}
          fetchCompanies={() => {
            queryClient.invalidateQueries(["client", "detail", String(id)]);
            queryClient.invalidateQueries(["crm", "client", String(id)]);
            queryClient.invalidateQueries(["companies"]);
          }}
        />
      </Modal>
    </div>
  );
};

export default ClientDetailPage;
